import React, { useState, useEffect } from 'react'
import { Box, Typography, IconButton, Card, CardContent, Divider, Button, Modal } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import CloseIcon from '@mui/icons-material/Close'
import { useNavigate } from 'react-router-dom'
import MainButton from '../../../components/MainButton'
import { useTemplateCreation } from '../../../contexts/TemplateCreationContext'
import PDFWebViewComponent from '../../../components/PDFWebViewComponent'
import { ContinueButton } from '../../../components/Buttons'
import { BackIconButton } from '../../../components/Buttons/NavigationButtons'

// Document Viewer Modal Component
const DocumentViewerModal = ({ open, onClose, fileUrl, fileName }) => {
    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="document-viewer-modal"
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Box
                sx={{
                    width: '90vw',
                    height: '90vh',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    position: 'relative',
                    boxShadow: 24,
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '16px 24px',
                        borderBottom: '1px solid #E5E7EB',
                        backgroundColor: '#F9FAFB',
                    }}
                >
                    <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>
                        {fileName || 'Document Viewer'}
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            padding: '4px',
                            '&:hover': { backgroundColor: '#E5E7EB' },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: '20px' }} />
                    </IconButton>
                </Box>

                {/* Document Viewer */}
                <Box sx={{ width: '100%', height: 'calc(100% - 73px)' }}>
                    {fileUrl && (
                        <PDFWebViewComponent
                            initialFile={fileUrl}
                            hasLink={false}
                        />
                    )}
                </Box>
            </Box>
        </Modal>
    )
}

export default function TemplateReviewComponent({
    onBackClick,
    onConfirm,
    loading,
    setLoading,
    success,
    setSuccess,
    trainingSetFiles,
    referenceFile,
    templateDetails
}) {
    const navigate = useNavigate()
    const { resetTemplateCreation, updateReferenceFile } = useTemplateCreation()
    const [documentViewerOpen, setDocumentViewerOpen] = useState(false)
    const [selectedDocument, setSelectedDocument] = useState(null)

    const handleViewFile = (file) => {
        setSelectedDocument(file)
        setDocumentViewerOpen(true)
    }

    const handleCloseViewer = () => {
        setDocumentViewerOpen(false)
        setSelectedDocument(null)
    }

    const handleConfirm = () => {
        // Call the onConfirm callback if provided
        if (onConfirm) {
            onConfirm()
        }
        
        // Reset the context as requested
        resetTemplateCreation()
    }

    const formatFileName = (fileName) => {
        if (!fileName) return ''
        // Remove org ID and path prefixes
        return fileName.split('/').pop() || fileName
    }

    useEffect(() => {
        console.log('templateDetails', templateDetails)
        console.log('referenceFile', referenceFile)
        console.log('trainingSetFiles', trainingSetFiles)
    }, [templateDetails])

    return (
        <Box sx={{ 
            width: '100%', 
            height: '100%', 
            padding: '40px',
            paddingTop: '120px', // Account for fixed stepper
            '@media (max-width: 768px)': {
                paddingTop: '100px',
            },
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
        }}>
            <Box sx={{
                position: 'fixed',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 999,
                overflowY: 'auto',
                maxHeight: 'calc(100vh - 122px)',
                width: 'clamp(600px, 60vw, 900px)',
                '@media (max-width: 768px)': {
                    width: 'calc(100vw - 32px)',
                    left: '16px',
                    transform: 'none',
                },
                scrollbarWidth: 'none', // Firefox
                msOverflowStyle: 'none', // IE and Edge
                '&::-webkit-scrollbar': {
                    display: 'none', // Chrome, Safari, Opera
                },
            }}>
                {/* Header */}
                <Box sx={{ mb: 4 }}>
                    <BackIconButton onClick={onBackClick} />
                    
                    <Box>
                        <Typography 
                            variant="h4" 
                            sx={{ 
                                fontSize: '24px',
                                fontWeight: 600,
                                color: '#111827',
                                mb: 1
                            }}
                        >
                            Review template
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 3, 
                    width: '100%',
                    maxWidth: '900px',
                    '@media (max-width: 768px)': {
                        maxWidth: '100%',
                        minWidth: 'unset',
                    },
                    '@media (min-width: 769px)': {
                        minWidth: '600px',
                    },
                    mb: '200px' 
                }}>
                    {/* Template Details Card */}
                    <Card sx={{ backgroundColor: '#FFFFFF', boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)', borderRadius: '8px' }}>
                        <CardContent sx={{ padding: '24px' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                                        <Box>
                                            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#374151', mb: 0.5 }}>
                                                Name
                                            </Typography>
                                            <Typography sx={{ fontSize: '14px', color: '#6B7280' }}>
                                                {templateDetails.name || 'Not specified'}
                                            </Typography>
                                        </Box>
                                        
                                        {/* <Box>
                                            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#374151', mb: 0.5 }}>
                                                Description
                                            </Typography>
                                            <Typography sx={{ fontSize: '14px', color: '#6B7280' }}>
                                                {templateDetails.description || 'Not specified'}
                                            </Typography>
                                        </Box> */}

                                        <Box>
                                            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#374151', mb: 0.5 }}>
                                                Type
                                            </Typography>
                                            <Typography sx={{ fontSize: '14px', color: '#6B7280' }}>
                                                {templateDetails.type || 'Not specified'}
                                            </Typography>
                                        </Box>

                                        {/* <Box>
                                            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#374151', mb: 0.5 }}>
                                                Style guide
                                            </Typography>
                                            <Typography sx={{ fontSize: '14px', color: '#6B7280' }}>
                                                {templateDetails.styleGuide || 'Not specified'}
                                            </Typography>
                                        </Box> */}
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Reference File Card */}
                    <Box>
                        <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#111827', mb: 2 }}>
                            Reference file
                        </Typography>
                        
                        <Card sx={{ backgroundColor: '#FFFFFF', boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)', borderRadius: '8px' }}>
                            <CardContent sx={{ padding: '24px' }}>
                                {referenceFile ? (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            padding: '12px 16px',
                                            backgroundColor: '#F9FAFB',
                                            borderRadius: '6px',
                                            border: '1px solid #E5E7EB',
                                        }}
                                    >
                                        <InsertDriveFileIcon sx={{ fontSize: 20, color: '#6B7280' }} />
                                        <Typography sx={{ fontSize: '14px', color: '#374151', flex: 1 }}>
                                            {formatFileName(referenceFile?.name || referenceFile)}
                                        </Typography>
                                        <Button
                                            onClick={() => handleViewFile(referenceFile?.name || referenceFile)}
                                            sx={{
                                                px: 2,
                                                py: 0.5,
                                                backgroundColor: 'transparent',
                                                color: '#4F46E5',
                                                borderRadius: '4px',
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                textTransform: 'none',
                                                minWidth: 'auto',
                                                '&:hover': {
                                                    backgroundColor: '#F3F4F6',
                                                },
                                            }}
                                        >
                                            View
                                        </Button>
                                    </Box>
                                ) : (
                                    <Typography sx={{ fontSize: '14px', color: '#6B7280', fontStyle: 'italic' }}>
                                        No reference file uploaded
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Box>

                    {/* Training Sets Section */}
                    <Box>
                        {trainingSetFiles.length > 0 ? (
                            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#111827', mb: 2 }}>
                                Example File(s)
                            </Typography> 
                        ) : (
                            null
                        )}
                        {trainingSetFiles.length > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                {trainingSetFiles.map((trainingSet, index) => {
                                    return (
                                        <Card key={trainingSet.id || index} sx={{ 
                                            backgroundColor: '#FFFFFF', 
                                            boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
                                            borderRadius: '8px'
                                        }}>
                                            <CardContent sx={{ padding: '24px' }}>
                                                <Box sx={{ display: 'flex', gap: 3 }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography sx={{ 
                                                            fontSize: '14px', 
                                                            fontWeight: 500, 
                                                            color: '#374151', 
                                                            mb: 2 
                                                        }}>
                                                            Example File {index + 1}
                                                        </Typography>
                                                        {trainingSet.outputFile ? (
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 2,
                                                                    padding: '8px 12px',
                                                                    backgroundColor: '#F9FAFB',
                                                                    borderRadius: '6px',
                                                                    border: '1px solid #E5E7EB',
                                                                }}
                                                            >
                                                                <InsertDriveFileIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                                                                <Typography sx={{ fontSize: '12px', color: '#374151', flex: 1 }}>
                                                                    {formatFileName(trainingSet.outputFile.name || trainingSet.outputFile)}
                                                                </Typography>
                                                                <Button
                                                                    onClick={() => handleViewFile(trainingSet.outputFile)}
                                                                    sx={{
                                                                        px: 1,
                                                                        py: 0.5,
                                                                        backgroundColor: 'transparent',
                                                                        color: '#4F46E5',
                                                                        borderRadius: '4px',
                                                                        fontSize: '10px',
                                                                        fontWeight: 500,
                                                                        textTransform: 'none',
                                                                        minWidth: 'auto',
                                                                        '&:hover': {
                                                                            backgroundColor: '#F3F4F6',
                                                                        },
                                                                    }}
                                                                >
                                                                    View
                                                                </Button>
                                                            </Box>
                                                        ) : (
                                                            <Typography sx={{ fontSize: '12px', color: '#6B7280', fontStyle: 'italic' }}>
                                                                No output file
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    )
                                })}
                            </Box>
                        ) : (
                            <Card sx={{ backgroundColor: '#FFFFFF', boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)', borderRadius: '8px' }}>
                                <CardContent sx={{ padding: '24px' }}>
                                    <Typography sx={{ fontSize: '14px', color: '#6B7280', fontStyle: 'italic' }}>
                                        No training sets created
                                    </Typography>
                                </CardContent>
                            </Card>
                        )}
                    </Box>
                </Box>
            </Box>

            <ContinueButton
                onClick={handleConfirm}
                text="Confirm"
                backgroundColor="#4F46E5"
                fontColor="#FFFFFF"
            />

            {/* Document Viewer Modal */}
            <DocumentViewerModal
                open={documentViewerOpen}
                onClose={handleCloseViewer}
                fileUrl={selectedDocument?.url || selectedDocument?.name}
                fileName={formatFileName(selectedDocument?.name || selectedDocument)}
            />
        </Box>
    )
}
